import { LM, pixelDistance, type LandmarkBuffer } from '../tracking/landmarks';
import { median } from './IrisScale';

/**
 * Anthropometric estimates in millimetres, derived from the metric depth and
 * the 2D landmarks. All values are sampled only on near-frontal frames and
 * aggregated with a rolling median, because they are per-person constants.
 *
 * Presented to the user as suggestions (frame size, PD) — never as a medical
 * measurement. A dispensing optician measures PD to the millimetre; this
 * estimate is typically within ±2–3 mm.
 */
export interface FaceMetricsSnapshot {
  /** Interpupillary distance, mm (null until enough frontal samples). */
  pdMm: number | null;
  /** Face width at temple level (landmarks 127–356), mm. */
  faceWidthMm: number | null;
  /** Head width near the ears (landmarks 234–454), mm — drives temple spread. */
  headWidthMm: number | null;
  samples: number;
}

/** Approximate depth offsets of the measured planes behind the cornea plane. */
const TEMPLE_PLANE_OFFSET_MM = 35;
const EAR_PLANE_OFFSET_MM = 75;

export class FaceMetrics {
  private readonly pd: number[] = [];
  private readonly faceWidth: number[] = [];
  private readonly headWidth: number[] = [];
  private readonly windowSize: number;

  constructor(windowSize = 45) {
    this.windowSize = windowSize;
  }

  /**
   * @param depthMm metric depth of the cornea/iris plane
   * @param focalPx camera focal length in pixels
   * @param yawDeg / pitchDeg head angles — samples are accepted only when frontal
   */
  update(buf: LandmarkBuffer, width: number, height: number, depthMm: number, focalPx: number, yawDeg: number, pitchDeg: number): void {
    if (Math.abs(yawDeg) > 10 || Math.abs(pitchDeg) > 12) return;
    const cosYaw = Math.cos((yawDeg * Math.PI) / 180);

    const pdPx = pixelDistance(buf, LM.RIGHT_IRIS_CENTER, LM.LEFT_IRIS_CENTER, width, height);
    push(this.pd, (pdPx * depthMm) / focalPx / cosYaw, this.windowSize);

    const faceWidthPx = pixelDistance(buf, LM.RIGHT_TEMPLE, LM.LEFT_TEMPLE, width, height);
    push(this.faceWidth, (faceWidthPx * (depthMm + TEMPLE_PLANE_OFFSET_MM)) / focalPx / cosYaw, this.windowSize);

    const headWidthPx = pixelDistance(buf, LM.RIGHT_EAR, LM.LEFT_EAR, width, height);
    push(this.headWidth, (headWidthPx * (depthMm + EAR_PLANE_OFFSET_MM)) / focalPx / cosYaw, this.windowSize);
  }

  snapshot(): FaceMetricsSnapshot {
    const enough = this.pd.length >= 10;
    return {
      pdMm: enough ? median(this.pd) : null,
      faceWidthMm: enough ? median(this.faceWidth) : null,
      headWidthMm: enough ? median(this.headWidth) : null,
      samples: this.pd.length,
    };
  }

  reset(): void {
    this.pd.length = 0;
    this.faceWidth.length = 0;
    this.headWidth.length = 0;
  }
}

function push(arr: number[], v: number, max: number): void {
  if (!Number.isFinite(v)) return;
  arr.push(v);
  if (arr.length > max) arr.shift();
}

/**
 * Clearance between the temple centre line and the skin at the ear (mm): half
 * the temple thickness plus a small margin, so the temple rests on the head
 * occluder instead of sinking into it.
 */
export const TEMPLE_CLEARANCE_MM = 2.3;

/**
 * Temple opening angle (degrees, positive = temples spread outwards) so that the
 * temple centre lines pass at the measured head width plus `clearanceMm` at the
 * ear. Small range: acetate temples flex a few degrees, they do not bend freely.
 */
export function templeSpreadDeg(frameWidthMm: number, headWidthMm: number | null, hingeToEarMm = 100, clearanceMm = 0): number {
  if (headWidthMm === null) return 0;
  const lateral = (headWidthMm - frameWidthMm) / 2 + clearanceMm; // mm each side
  const angle = (Math.asin(Math.max(-1, Math.min(1, lateral / hingeToEarMm))) * 180) / Math.PI;
  return Math.max(-3, Math.min(10, angle));
}
